import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../../firebase/firebase';
import { onAuthStateChanged, signOut, getIdToken } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import VendorProfileForm from '../../components/vendor/VendorProfileForm';
import OfferForm from '../../components/vendor/OfferForm';
import QRScanner from '../../components/vendor/QRScanner';
import VendorOverview from '../../components/vendor/VendorOverview';
import VendorStaff from './VendorStaff';
import VendorRedemptions from './VendorRedemptions';
import WIcon from '../../components/shared/WIcon';
import TopHeader from '../../components/shared/TopHeader';
import '../../styles/VendorDashboard.css';
import logo from '../../assets/logo_white.png';

// Brand vendor theme — aligned with design system
const vendorTheme = {
  gradient: 'linear-gradient(135deg, #00bf63 0%, #009950 100%)',
  primary: '#00bf63',
  primaryHover: '#009950'
};

const VendorDashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check authentication state on component mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user || !user.email) {
        setCurrentUser(null);
        navigate('/vendor/login');
        setLoading(false);
        return;
      }

      try {
        await getIdToken(user, true);

        // Whitelist check — vendor must have a vendorAdmins/{email} entry.
        const email = user.email.toLowerCase();
        const whitelistSnap = await getDoc(doc(db, 'vendorAdmins', email));
        if (!whitelistSnap.exists()) {
          await signOut(auth);
          navigate('/vendor/login');
          setLoading(false);
          return;
        }

        setCurrentUser(user);

        const vendorSnap = await getDoc(doc(db, 'vendors', user.uid));
        if (vendorSnap.exists()) {
          setVendorData(vendorSnap.data());
        }

        setAuthError(null);
      } catch (error) {
        console.error('Auth token validation error:', error);
        setAuthError('Your session is invalid or expired. Please sign in again.');
        await signOut(auth);
        navigate('/vendor/login');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);
  
  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // Navigate to login page after sign out
      navigate('/vendor/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  // Close mobile menu when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);
  
  // Show loading indicator while checking auth state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading vendor dashboard...</p>
      </div>
    );
  }
  
  // If not authenticated, redirect to login
  if (!currentUser) {
    return <Navigate to="/vendor/login" />;
  }
  
  // Display auth error if any
  if (authError) {
    return (
      <div className="auth-error-container">
        <div className="auth-error">
          <h2>Authentication Error</h2>
          <p>{authError}</p>
          <button onClick={() => navigate('/vendor/login')} style={{ background: vendorTheme.gradient, border: 'none' }}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }
  
  
  const isOverviewPage = location.pathname === '/vendor/dashboard' || location.pathname === '/vendor/dashboard/';
  const isOffersPage = location.pathname.includes('/offers');
  const isScannerPage = location.pathname.includes('/scanner');
  const isProfilePage = location.pathname.includes('/profile');
  const isStaffPage = location.pathname.includes('/staff');
  const isRedemptionsPage = location.pathname.includes('/redemptions');

  const businessName = vendorData?.name || vendorData?.businessName || 'Your Business';
  const initials = (businessName || 'V').split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const pageTitle = isOverviewPage ? 'Dashboard'
    : isOffersPage ? 'My Offers'
    : isScannerPage ? 'QR Scanner'
    : isStaffPage ? 'Staff'
    : isRedemptionsPage ? 'Redemptions'
    : isProfilePage ? 'Business Profile'
    : 'Dashboard';

  const headerActions = isOverviewPage || isOffersPage ? (
    <button
      type="button"
      className="loysta-topbar-add-btn"
      onClick={() => navigate('/vendor/dashboard/scanner')}
    >
      <WIcon name="qrCode" size={15} color="white" />
      Scan QR Code
    </button>
  ) : null;

  return (
    <div className="loysta-app vendor-dashboard-container">
      {/* Dark sidebar */}
      <aside className={`loysta-sidebar vendor-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="loysta-sidebar-header">
          <img src={logo} alt="Loysta" />
          <button
            type="button"
            className="close-sidebar-btn"
            onClick={toggleMobileMenu}
            aria-label="Close menu"
          >
            <WIcon name="x" size={18} color="white" />
          </button>
        </div>

        <div className="loysta-sidebar-portal-label">
          <span className="dot" />
          Vendor Portal
        </div>

        <nav className="loysta-sidebar-nav">
          <NavLink
            to="/vendor/dashboard"
            end
            className={({ isActive }) => `loysta-nav-link${isActive ? ' active' : ''}`}
          >
            <WIcon name="dashboard" size={17} />
            <span>Dashboard</span>
          </NavLink>

          <NavLink
            to="/vendor/dashboard/offers"
            className={({ isActive }) => `loysta-nav-link${isActive ? ' active' : ''}`}
          >
            <WIcon name="tag" size={17} />
            <span>My Offers</span>
          </NavLink>

          <NavLink
            to="/vendor/dashboard/scanner"
            className={({ isActive }) => `loysta-nav-link${isActive ? ' active' : ''}`}
          >
            <WIcon name="qrCode" size={17} />
            <span>QR Scanner</span>
          </NavLink>

          <NavLink
            to="/vendor/dashboard/staff"
            className={({ isActive }) => `loysta-nav-link${isActive ? ' active' : ''}`}
          >
            <WIcon name="users" size={17} />
            <span>Staff</span>
          </NavLink>

          <NavLink
            to="/vendor/dashboard/redemptions"
            className={({ isActive }) => `loysta-nav-link${isActive ? ' active' : ''}`}
          >
            <WIcon name="check" size={17} />
            <span>Redemptions</span>
          </NavLink>

          <NavLink
            to="/vendor/dashboard/profile"
            className={({ isActive }) => `loysta-nav-link${isActive ? ' active' : ''}`}
          >
            <WIcon name="settings" size={17} />
            <span>Settings</span>
          </NavLink>
        </nav>

        <div className="loysta-sidebar-footer">
          <div className="loysta-sidebar-user">
            {(() => {
              if (vendorData?.images && vendorData?.mainPhotoId) {
                const mainPhoto = vendorData.images.find(img => img.id === vendorData.mainPhotoId);
                if (mainPhoto) {
                  return <img src={mainPhoto.url} alt={businessName} className="loysta-sidebar-user-avatar" style={{ objectFit: 'cover' }} />;
                }
              }
              if (vendorData?.logoUrl) {
                return <img src={vendorData.logoUrl} alt={businessName} className="loysta-sidebar-user-avatar" style={{ objectFit: 'cover' }} />;
              }
              return <div className="loysta-sidebar-user-avatar">{initials}</div>;
            })()}
            <div className="loysta-sidebar-user-info">
              <div className="loysta-sidebar-user-name">{businessName}</div>
              <div className="loysta-sidebar-user-sub">{currentUser.email}</div>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="loysta-sidebar-logout-btn"
              title="Sign out"
            >
              <WIcon name="logout" size={15} />
            </button>
          </div>
        </div>
      </aside>

      <div className="loysta-main vendor-main">
        <TopHeader title={pageTitle} userInitials={initials} actions={headerActions} />
        <main className="loysta-content vendor-content">
          <Routes>
            <Route index element={<VendorOverview />} />
            <Route path="offers" element={<OfferForm />} />
            <Route path="scanner" element={<QRScanner />} />
            <Route path="staff" element={<VendorStaff />} />
            <Route path="redemptions" element={<VendorRedemptions />} />
            <Route path="profile" element={<VendorProfileForm />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default VendorDashboard;